import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef } from 'react';
const sizeStyles = {
    sm: { height: 28, padding: '0 var(--space-3)', fontSize: 'var(--font-size-sm)' },
    md: { height: 36, padding: '0 var(--space-4)', fontSize: 'var(--font-size-base)' },
    lg: { height: 44, padding: '0 var(--space-6)', fontSize: 'var(--font-size-md)' },
};
function getVariantStyles(variant) {
    switch (variant) {
        case 'primary':
            return {
                base: {
                    backgroundColor: 'var(--color-accent)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                },
                hover: {
                    backgroundColor: 'var(--color-accent-hover)',
                    boxShadow: '0 0 12px var(--color-accent-glow)',
                },
            };
        case 'secondary':
            return {
                base: {
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text)',
                    border: '1px solid var(--color-border-strong)',
                },
                hover: {
                    backgroundColor: 'var(--color-surface-raised)',
                },
            };
        case 'ghost':
            return {
                base: {
                    backgroundColor: 'transparent',
                    color: 'var(--color-text)',
                    border: '1px solid transparent',
                },
                hover: {
                    backgroundColor: 'var(--color-surface-sunken)',
                },
            };
        case 'danger':
            return {
                base: {
                    backgroundColor: 'var(--color-danger)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                },
                hover: {
                    opacity: 0.9,
                },
            };
        case 'link':
            return {
                base: {
                    backgroundColor: 'transparent',
                    color: 'var(--color-accent)',
                    border: 'none',
                    padding: 0,
                    height: 'auto',
                },
                hover: {
                    color: 'var(--color-accent-hover)',
                },
            };
    }
}
function Spinner() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", style: { animation: 'spin 0.8s linear infinite' }, children: [_jsx("circle", { cx: "8", cy: "8", r: "6", stroke: "currentColor", strokeWidth: "2", opacity: "0.3" }), _jsx("path", { d: "M8 2a6 6 0 0 1 6 6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }), _jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg) } }` })] }));
}
export const Button = forwardRef(function Button(props, ref) {
    const { variant = 'primary', size = 'md', loading = false, iconLeft, iconRight, fullWidth, children, style: styleProp, href, ...rest } = props;
    const variantStyles = getVariantStyles(variant);
    const baseStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        fontFamily: 'var(--font-family)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: 1,
        borderRadius: 'var(--radius-md)',
        cursor: loading ? 'default' : 'pointer',
        textDecoration: 'none',
        transition: `background-color 200ms, color 200ms, border-color 200ms, box-shadow 200ms, opacity 200ms`,
        opacity: loading ? 0.5 : 1,
        pointerEvents: loading ? 'none' : 'auto',
        width: fullWidth ? '100%' : undefined,
        ...sizeStyles[size],
        ...variantStyles.base,
        ...styleProp,
    };
    const handleMouseEnter = (e) => {
        if (loading)
            return;
        const el = e.currentTarget;
        for (const [key, val] of Object.entries(variantStyles.hover)) {
            el.style.setProperty(key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`), String(val));
        }
    };
    const handleMouseLeave = (e) => {
        if (loading)
            return;
        const el = e.currentTarget;
        const base = { ...sizeStyles[size], ...variantStyles.base };
        for (const key of Object.keys(variantStyles.hover)) {
            const kebab = key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`);
            const val = base[key];
            el.style.setProperty(kebab, val != null ? String(val) : '');
        }
    };
    const content = (_jsxs(_Fragment, { children: [loading ? _jsx(Spinner, {}) : iconLeft, _jsx("span", { children: children }), !loading && iconRight] }));
    if (href) {
        return (_jsx("a", { ref: ref, href: href, style: baseStyle, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, ...rest, children: content }));
    }
    return (_jsx("button", { ref: ref, type: "button", disabled: loading, style: baseStyle, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, ...rest, children: content }));
});
